import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setToken, getToken } from "./api";

const AuthContext = createContext(null);

/**
 * Provider di autenticazione.
 * Espone: utente { username, ruolo }, ruolo, stato di caricamento, login(), logout().
 * Al mount, se c'è un token, ripristina la sessione via api.me().
 */
export function AuthProvider({ children }) {
  const [utente, setUtente] = useState(null);
  const [caricamento, setCaricamento] = useState(true);

  const logout = useCallback(() => {
    setToken(null);
    setUtente(null);
  }, []);

  const login = useCallback(async (username, password) => {
    const res = await api.login(username, password);
    setToken(res.token);
    setUtente({ username: res.username, ruolo: res.ruolo });
    return res;
  }, []);

  // Ripristino sessione all'avvio
  useEffect(() => {
    let annullato = false;
    (async () => {
      if (!getToken()) { setCaricamento(false); return; }
      try {
        const me = await api.me();
        if (!annullato) setUtente({ username: me.username, ruolo: me.ruolo });
      } catch {
        if (!annullato) { setToken(null); setUtente(null); }
      } finally {
        if (!annullato) setCaricamento(false);
      }
    })();
    return () => { annullato = true; };
  }, []);

  // Logout forzato quando l'API riceve un 401
  useEffect(() => {
    const onLogout = () => setUtente(null);
    window.addEventListener("auth:logout", onLogout);
    return () => window.removeEventListener("auth:logout", onLogout);
  }, []);

  const ruolo = utente?.ruolo || null;

  // Permessi di scrittura per sezione
  const canEdit = useCallback((sezione) => {
    if (ruolo === "admin") return true;
    if (ruolo === "compilatore") return sezione === "dettaglio";
    return false; // visualizzatore o non autenticato
  }, [ruolo]);

  const value = { utente, ruolo, caricamento, login, logout, canEdit };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth deve essere usato dentro <AuthProvider>");
  return ctx;
}
